import api from './api'
import { PaginationResponse } from '../types/api'

type ApiSuccessResponse<T> = {
  success?: boolean
  data: T
}

export interface Brand {
  id: string
  name: string
  slug: string
  description?: string
  website?: string
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface BrandFormValues {
  name: string
  description?: string
  website?: string
  isActive: boolean
  sortOrder: number
}

type ListParams = {
  search?: string
  isActive?: boolean
  page?: number
  limit?: number
}

export type AdminBrandListResponse = {
  brands: Brand[]
  pagination: PaginationResponse
}

const adminBrandService = {
  async list(params: ListParams = {}): Promise<AdminBrandListResponse> {
    const { search, isActive, page, limit } = params

    const queryParams: Record<string, unknown> = {}

    if (search) queryParams.search = search
    if (isActive !== undefined) queryParams.isActive = isActive
    if (page) queryParams.page = page
    if (limit) queryParams.limit = limit

    const response = await api.get<ApiSuccessResponse<AdminBrandListResponse>>(
      '/admin/brands',
      { params: queryParams }
    )

    return response.data.data
  },

  async getById(brandId: string): Promise<Brand> {
    const response = await api.get<ApiSuccessResponse<Brand>>(
      `/admin/brands/${brandId}`
    )

    return response.data.data
  },

  async create(payload: BrandFormValues, logoFile?: File): Promise<Brand> {
    const formData = new FormData()

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'boolean') {
          formData.append(key, value.toString())
        } else {
          formData.append(key, value.toString())
        }
      }
    })

    if (logoFile) {
      formData.append('logo', logoFile)
    }

    const response = await api.post<ApiSuccessResponse<Brand>>(
      '/admin/brands',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return response.data.data
  },

  async update(
    brandId: string,
    payload: BrandFormValues,
    logoFile?: File
  ): Promise<Brand> {
    const formData = new FormData()

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'boolean') {
          formData.append(key, value.toString())
        } else {
          formData.append(key, value.toString())
        }
      }
    })

    if (logoFile) {
      formData.append('logo', logoFile)
    }

    const response = await api.put<ApiSuccessResponse<Brand>>(
      `/admin/brands/${brandId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return response.data.data
  },

  async updateStatus(brandId: string, isActive: boolean): Promise<Brand> {
    const response = await api.patch<ApiSuccessResponse<Brand>>(
      `/admin/brands/${brandId}/status`,
      { isActive }
    )

    return response.data.data
  },

  async remove(brandId: string): Promise<void> {
    await api.delete(`/admin/brands/${brandId}`)
  }
}

export default adminBrandService

